/**
 * Pure helpers for the "Move to Closed Won" acquisition modal.
 *
 * When a deal is dragged/advanced into the Closed-Won stage we ask the user to
 * confirm the acquisition: ownership (→ `pipeline_properties.is_owned`), the
 * owned date, the purchase price, and — when financing is enabled — the
 * acquisition loan value + EMI date. This module turns those answers into the
 * `property_setting` patch and resolves the owned-date default. It has no React
 * or Supabase dependency so it can be unit-tested in isolation.
 */
import { termInMonths } from "@/lib/calculators";
import type { StrategyPatch } from "@/lib/property-setting";
import type {
  BrrrrSettings,
  FlipSettings,
  LoanInputs,
  RentalSettings,
  Strategy,
} from "@/types/pipeline";

/** The strategy-blob loan slot whose value is confirmed at acquisition. */
export interface LoanSlot {
  /** Which nested loan field holds the acquisition loan. */
  field: "hard_money" | "long_term";
  /** Human label for the modal section. */
  label: string;
}

/**
 * Default loan terms per acquisition slot — mirrors `loanViewMeta` in
 * `financing-metrics.ts`. Used to seed the modal's rate/term/points (and LTV)
 * when a deal has no loan saved yet.
 */
const SLOT_DEFAULTS: Record<
  Strategy,
  { ltv: number; rate: number; term: number; points: number }
> = {
  Flip: { ltv: 85, rate: 11, term: 12, points: 2 },
  BRRRR: { ltv: 85, rate: 11, term: 12, points: 2 },
  Rental: { ltv: 80, rate: 7, term: 30, points: 1 },
};

/** Acquisition loan slot for a strategy (Flip/BRRRR = hard money, Rental = conventional). */
export function loanSlotFor(strategy: Strategy): LoanSlot {
  if (strategy === "Rental") return { field: "long_term", label: "Loan" };
  return { field: "hard_money", label: "Hard Money Loan" };
}

/** Default entry/display unit for the acquisition loan's term (hard money = months). */
export function acquisitionTermUnit(strategy: Strategy): "months" | "years" {
  return loanSlotFor(strategy).field === "long_term" ? "years" : "months";
}

/**
 * Seed the term editor (canonical months + entry unit) for the acquisition
 * loan — from the saved loan when present, else the strategy default.
 */
export function termSeedFor(
  settings: AcquisitionSettings | null | undefined,
  strategy: Strategy,
): { months: number; unit: "months" | "years" } {
  const loan = currentLoanFor(settings, strategy);
  const unit = acquisitionTermUnit(strategy);
  const months = loan
    ? termInMonths(loan, unit)
    : SLOT_DEFAULTS[strategy].term * (unit === "years" ? 12 : 1);
  return { months, unit: loan?.term_unit ?? unit };
}

/**
 * `loans.loan_type` for the acquisition loan that's materialised when the deal
 * closes. Flip/BRRRR acquire with hard money; Rental holds a conventional loan.
 */
export function acquisitionLoanType(
  strategy: Strategy,
): "hard_money" | "conventional" {
  return strategy === "Rental" ? "conventional" : "hard_money";
}

/** The strategy settings this module reads (subset of the composed deal settings). */
export interface AcquisitionSettings {
  flip?: FlipSettings;
  brrrr?: BrrrrSettings;
  rental?: RentalSettings;
}

/** The loan currently saved for the acquisition slot, if any. */
export function currentLoanFor(
  settings: AcquisitionSettings | null | undefined,
  strategy: Strategy,
): LoanInputs | undefined {
  const s = settings ?? {};
  if (strategy === "Rental") return s.rental?.long_term;
  if (strategy === "Flip") return s.flip?.hard_money;
  return s.brrrr?.hard_money;
}

/**
 * Prefill dollar value for the acquisition loan = purchase × LTV. Uses the
 * saved LTV when present, else the strategy default.
 */
export function loanValueFor(
  settings: AcquisitionSettings | null | undefined,
  strategy: Strategy,
  purchase: number,
): number {
  const loan = currentLoanFor(settings, strategy);
  const ltv = Number(loan?.ltv) || SLOT_DEFAULTS[strategy].ltv;
  return Math.round(Math.max(0, purchase) * (ltv / 100));
}

/** Prefill interest rate (%) for the acquisition loan — saved value, else default. */
export function loanRateFor(
  settings: AcquisitionSettings | null | undefined,
  strategy: Strategy,
): number {
  return currentLoanFor(settings, strategy)?.rate ?? SLOT_DEFAULTS[strategy].rate;
}

/** Prefill loan points (%) for the acquisition loan — saved value, else default. */
export function loanPointsFor(
  settings: AcquisitionSettings | null | undefined,
  strategy: Strategy,
): number {
  return currentLoanFor(settings, strategy)?.points ?? SLOT_DEFAULTS[strategy].points;
}

/** Owned date the user entered, or `today` (YYYY-MM-DD) when left blank. */
export function resolveOwnedDate(
  input: string | null | undefined,
  today: string,
): string {
  return (input ?? "").trim() || today;
}

interface BuildPatchArgs {
  strategy: Strategy | null;
  settings: AcquisitionSettings | null | undefined;
  /** Confirmed purchase price (may be null when blank). */
  purchase: number | null;
  /** True when financing is enabled for the deal (loan fields were collected). */
  financed: boolean;
  /** Confirmed acquisition loan value (dollars). */
  loanValue: number | null;
  /** EMI date (YYYY-MM-DD) or "" — stored on the loan's `loan_date`. */
  emiDate: string;
  /** Confirmed term in canonical months. Omit to keep the existing/default term. */
  termMonths?: number | null;
  /** Entry/display unit for the term. */
  termUnit?: "months" | "years";
  /** Confirmed interest rate (%). Omit to keep the existing/default rate. */
  rate?: number | null;
  /** Confirmed loan points (%). Omit to keep the existing/default points. */
  points?: number | null;
}

/**
 * Build the `property_setting` patch from the modal answers: always writes the
 * purchase price and the acquisition loan blob, stamping `is_enabled` to record
 * the financing choice. When financed, the confirmed loan value (→ LTV against
 * the purchase price) and EMI date are folded in; when not financed, existing
 * terms (or the strategy defaults) are preserved with `is_enabled: false`.
 */
export function buildClosedWonPatch(
  args: BuildPatchArgs,
): { purchase_price: number | null } & StrategyPatch {
  const patch: { purchase_price: number | null } & StrategyPatch = {
    purchase_price: args.purchase,
  };

  const { strategy } = args;
  if (!strategy) return patch;

  const existing = currentLoanFor(args.settings, strategy);
  const defaults = SLOT_DEFAULTS[strategy];

  let ltv: number;
  let loanDate: string | null;
  if (args.financed) {
    const base = Math.max(0, args.purchase ?? 0);
    const value = Math.max(0, args.loanValue ?? 0);
    // Round to 5dp so the dollar value the user typed round-trips back to the
    // same amount (matches the LoanEditDrawer's $→% conversion).
    ltv =
      base > 0
        ? Math.round((value / base) * 100 * 1e5) / 1e5
        : (existing?.ltv ?? defaults.ltv);
    loanDate = args.emiDate || null;
  } else {
    // Financing disabled — record the choice, keep any existing terms.
    ltv = existing?.ltv ?? defaults.ltv;
    loanDate = existing?.loan_date ?? null;
  }

  // Term: prefer the confirmed canonical months, else the existing/default term.
  const slotUnit = acquisitionTermUnit(strategy);
  const months =
    args.termMonths != null && Number.isFinite(args.termMonths)
      ? Math.max(0, Math.round(args.termMonths))
      : existing
        ? termInMonths(existing, slotUnit)
        : slotUnit === "years"
          ? defaults.term * 12
          : defaults.term;
  const legacyTerm =
    slotUnit === "years" ? Math.max(1, Math.round(months / 12)) : Math.max(1, months);

  // Rate/points: prefer the confirmed value when financing was collected, else
  // keep the existing (or strategy-default) terms.
  const rate =
    args.financed && args.rate != null && Number.isFinite(args.rate)
      ? Math.max(0, args.rate)
      : (existing?.rate ?? defaults.rate);
  const points =
    args.financed && args.points != null && Number.isFinite(args.points)
      ? Math.max(0, args.points)
      : (existing?.points ?? defaults.points);

  const loan: LoanInputs = {
    ltv,
    rate,
    term: legacyTerm,
    term_months: months,
    term_unit: args.termUnit ?? existing?.term_unit ?? slotUnit,
    points,
    loan_date: loanDate,
    is_enabled: args.financed,
  };

  const s = args.settings ?? {};
  if (strategy === "Rental") {
    patch.rental = { ...s.rental, long_term: loan };
  } else if (strategy === "Flip") {
    patch.flip = { ...s.flip, hard_money: loan };
  } else {
    patch.brrrr = { ...s.brrrr, hard_money: loan };
  }
  return patch;
}
